/**
 * SectionShell + SectionHeader — collapsible section primitives shared
 * by the Unified Appointment modal and the Block Time modal so both
 * pages render visually identical sections (same header pill, same
 * body padding/border).
 *
 * Extracted from `UnifiedAppointmentModal.tsx` (legacy section pattern
 * mirroring `services/scheduler-appt-details.php`).
 */
import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface SectionHeaderProps {
  title: string;
  children?: React.ReactNode;
  collapsed?: boolean;
  onToggle?: () => void;
}

export function SectionHeader({ title, children, collapsed, onToggle }: SectionHeaderProps) {
  return (
    <div
      className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-t-md cursor-pointer select-none"
      onClick={onToggle}
      role={onToggle ? 'button' : undefined}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">{title}</h3>
      <div className="flex items-center gap-2">
        {children}
        {onToggle ? (collapsed ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronUp className="h-4 w-4 text-slate-400" />) : null}
      </div>
    </div>
  );
}

interface SectionShellProps {
  title: string;
  collapsed?: boolean;
  onToggle?: () => void;
  right?: React.ReactNode;
  children: React.ReactNode;
}

export function SectionShell({ title, collapsed, onToggle, right, children }: SectionShellProps) {
  return (
    <section className="mb-4">
      <SectionHeader title={title} collapsed={collapsed} onToggle={onToggle}>
        {right}
      </SectionHeader>
      {!collapsed && (
        <div className="p-3 border border-t-0 border-slate-200 rounded-b-md bg-white">{children}</div>
      )}
    </section>
  );
}
